import { useQuery } from "@tanstack/react-query";
import { apiClient, AnalyticsOverview } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { Sidebar } from "@/components/Sidebar";
import {
  TrendingUp,
  TrendingDown,
  Target,
  CheckCircle2,
  Clock,
  Users,
  MessageSquare,
  BarChart3,
  PieChart,
  Activity,
  Loader2,
  Youtube,
  TrendingUp as TrendingUpIcon,
  Eye,
  Heart,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

export default function AnalyticsPage() {
  const { user } = useAuthStore();

  const { data: analytics, isLoading } = useQuery({
    queryKey: ["analytics", user?._id],
    queryFn: () => apiClient.getAnalyticsOverview(),
    enabled: !!user?._id,
  });

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-yellow-600";
    return "text-orange-600";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return "Ready for Market";
    if (score >= 60) return "Strong Foundation";
    if (score >= 40) return "In Progress";
    return "Early Stage";
  };

  const getTrafficLightColor = (status: string) => {
    if (status === "green") return "bg-green-500";
    if (status === "yellow") return "bg-yellow-500";
    return "bg-red-500";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen overflow-hidden selection:bg-primary selection:text-primary-foreground relative bg-background text-foreground flex font-ibm">
        <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:32px_32px]"></div>
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent"></div>
        </div>
        <Sidebar />
        <div className="flex flex-1 pl-0 pt-16 md:pt-0 transition-all duration-300 h-screen w-full">
          <main className="flex-1 min-w-0 flex flex-col z-10 overflow-y-auto no-scrollbar bg-transparent h-full py-6 px-4 md:pt-8 md:pr-10 md:pb-8 md:pl-10 relative">
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen overflow-hidden selection:bg-primary selection:text-primary-foreground relative bg-background text-foreground flex font-ibm">
        <Sidebar />
        <div className="flex flex-1 pl-0 pt-16 md:pt-0 transition-all duration-300 h-screen w-full">
          <main className="flex-1 min-w-0 flex flex-col z-10 overflow-y-auto no-scrollbar bg-transparent h-full py-6 px-4 md:pt-8 md:pr-10 md:pb-8 md:pl-10 relative">
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">No analytics data available</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden selection:bg-primary selection:text-primary-foreground relative bg-background text-foreground flex font-ibm">
      {/* Background Grid Animation */}

      <Sidebar />

      <div className="flex flex-1 pl-0 pt-16 md:pt-0 transition-all duration-300 h-screen w-full">
        <main className="flex-1 min-w-0 flex flex-col z-10 overflow-y-auto no-scrollbar bg-transparent h-full py-6 px-4 md:pt-8 md:pr-10 md:pb-8 md:pl-10 relative">
          {/* Header */}
          <header className="flex flex-col md:flex-row mb-6 md:mb-10 items-center justify-between gap-4 md:gap-6">
            <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div>
                <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet">
                  Analytics Overview
                </h1>
                <p className="text-muted-foreground font-ibm text-sm mt-1 max-w-sm">
                  Track your progress and insights across all projects
                </p>
              </div>
            </div>
          </header>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Projects */}
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Total Projects
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-cabinet">{analytics.projects.total}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.projects.recent_count} created recently
                </p>
              </CardContent>
            </Card>

            {/* Average Score */}
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Average Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold font-cabinet ${getScoreColor(analytics.scores.average_score)}`}>
                  {analytics.scores.average_score}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {getScoreLabel(analytics.scores.average_score)}
                </p>
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-cabinet">{analytics.recommendations.total}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.recommendations.completion_rate}% completed
                </p>
              </CardContent>
            </Card>

            {/* Collaborators */}
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Collaborators
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-cabinet">{analytics.collaboration.total_collaborators}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.collaboration.total_sticky_notes} sticky notes
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Score Trends Chart */}
          <Card className="bg-card/60 backdrop-blur-sm border-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Score Trends (Last 6 Months)
              </CardTitle>
              <CardDescription>Track your average Artie Score over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics.scores.trends.map((trend, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-20 text-sm text-muted-foreground">{trend.month}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Progress value={trend.average_score} className="flex-1" />
                        <span className={`text-sm font-medium ${getScoreColor(trend.average_score)}`}>
                          {trend.average_score}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground w-16 text-right">
                      {trend.count} {trend.count === 1 ? 'score' : 'scores'}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Score Distribution */}
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Score Distribution
                </CardTitle>
                <CardDescription>Projects by score range</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Ready for Market (80-100)</span>
                    <span className="text-sm font-medium text-green-600">
                      {analytics.scores.distribution.ready_for_market}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Strong Foundation (60-79)</span>
                    <span className="text-sm font-medium text-blue-600">
                      {analytics.scores.distribution.strong_foundation}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">In Progress (40-59)</span>
                    <span className="text-sm font-medium text-yellow-600">
                      {analytics.scores.distribution.in_progress}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Early Stage (0-39)</span>
                    <span className="text-sm font-medium text-orange-600">
                      {analytics.scores.distribution.early_stage}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Project Status */}
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Project Status
                </CardTitle>
                <CardDescription>Projects by current status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(analytics.projects.by_status).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{status.replace(/_/g, ' ')}</span>
                      <span className="text-sm font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Traffic Lights */}
          <Card className="bg-card/60 backdrop-blur-sm border-border mb-6">
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Traffic light status across all projects</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Reach */}
                <div>
                  <h4 className="text-sm font-medium mb-3">Reach</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('green')}`}></div>
                        <span className="text-sm">Green</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.reach.green}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('yellow')}`}></div>
                        <span className="text-sm">Yellow</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.reach.yellow}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('red')}`}></div>
                        <span className="text-sm">Red</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.reach.red}</span>
                    </div>
                  </div>
                </div>

                {/* Brand Fit */}
                <div>
                  <h4 className="text-sm font-medium mb-3">Brand Fit</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('green')}`}></div>
                        <span className="text-sm">Green</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.brand_fit.green}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('yellow')}`}></div>
                        <span className="text-sm">Yellow</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.brand_fit.yellow}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('red')}`}></div>
                        <span className="text-sm">Red</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.brand_fit.red}</span>
                    </div>
                  </div>
                </div>

                {/* Production Readiness */}
                <div>
                  <h4 className="text-sm font-medium mb-3">Production Readiness</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('green')}`}></div>
                        <span className="text-sm">Green</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.production_readiness.green}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('yellow')}`}></div>
                        <span className="text-sm">Yellow</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.production_readiness.yellow}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getTrafficLightColor('red')}`}></div>
                        <span className="text-sm">Red</span>
                      </div>
                      <span className="text-sm font-medium">{analytics.traffic_lights.production_readiness.red}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recommendations Progress */}
          <Card className="bg-card/60 backdrop-blur-sm border-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Recommendations Progress
              </CardTitle>
              <CardDescription>Track your recommendation completion</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Overall Completion</span>
                  <span className="text-sm font-medium">{analytics.recommendations.completion_rate}%</span>
                </div>
                <Progress value={analytics.recommendations.completion_rate} className="h-2" />
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
                  {Object.entries(analytics.recommendations.by_status).map(([status, count]) => (
                    <div key={status} className="text-center">
                      <div className="text-2xl font-bold font-cabinet">{count}</div>
                      <div className="text-xs text-muted-foreground capitalize mt-1">
                        {status.replace(/_/g, ' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Budget Overview */}
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Budget Overview
                </CardTitle>
                <CardDescription>Total brand revenue targets and viability</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Total Target</div>
                    <div className="text-3xl font-bold font-cabinet">
                      ${(analytics.budget.total_target || 0).toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="space-y-2 mt-4">
                    <div className="text-sm font-medium mb-2">Viability Distribution</div>
                    {Object.entries(analytics.budget.viability_distribution).map(([viability, count]) => (
                      <div key={viability} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{viability}</span>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Channel Summary */}
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Connected Channels
                </CardTitle>
                <CardDescription>Your social media accounts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Total Channels</div>
                    <div className="text-3xl font-bold font-cabinet">
                      {analytics.channels.total_channels}
                    </div>
                  </div>
                  
                  <div className="space-y-2 mt-4">
                    <div className="text-sm font-medium mb-2">Platforms</div>
                    {analytics.channels.social_profiles.map((profile, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{profile.platform}</span>
                        <span className="text-sm font-medium">{profile.handle.startsWith('@') ? profile.handle : `@${profile.handle}`}</span>
                      </div>
                    ))}
                    {analytics.channels.youtube_channel && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm">YouTube (OAuth)</span>
                        <span className="text-sm font-medium">{analytics.channels.youtube_channel.title}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Channel Analytics Details */}
          {analytics.channels.total_channels > 0 && (
            <Card className="bg-card/60 backdrop-blur-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Channel Analytics
                </CardTitle>
                <CardDescription>Detailed metrics for your connected channels</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* YouTube OAuth Channel */}
                  {analytics.channels.youtube_channel && (
                    <div className="p-4 border border-border rounded-lg bg-card/40">
                      <div className="flex items-start gap-4">
                        {analytics.channels.youtube_channel.thumbnail_url ? (
                          <img
                            src={analytics.channels.youtube_channel.thumbnail_url}
                            alt={analytics.channels.youtube_channel.title}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center">
                            <Youtube className="w-8 h-8 text-white" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{analytics.channels.youtube_channel.title}</h4>
                            <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">YouTube</span>
                          </div>
                          {analytics.channels.youtube_channel.custom_url && (
                            <p className="text-sm text-muted-foreground mb-3">
                              {analytics.channels.youtube_channel.custom_url}
                            </p>
                          )}
                          {analytics.channels.youtube_channel.stats && (
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <div className="text-xs text-muted-foreground">Subscribers</div>
                                <div className="text-lg font-semibold">
                                  {(analytics.channels.youtube_channel.stats.subscriber_count || 0).toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground">Total Views</div>
                                <div className="text-lg font-semibold">
                                  {(analytics.channels.youtube_channel.stats.view_count || 0).toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground">Videos</div>
                                <div className="text-lg font-semibold">
                                  {(analytics.channels.youtube_channel.stats.video_count || 0).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Social Profiles */}
                  {analytics.channels.social_profiles.map((profile, index) => {
                    // Collect all available metrics
                    const metrics: Array<{ label: string; value: string | number; icon?: any }> = [];
                    
                    // Basic metrics
                    if (profile.follower_count !== undefined && profile.follower_count !== null) {
                      metrics.push({ label: 'Followers', value: profile.follower_count, icon: Users });
                    }
                    if (profile.avg_views !== undefined && profile.avg_views !== null) {
                      metrics.push({ label: 'Avg Views', value: profile.avg_views, icon: Eye });
                    }
                    if (profile.total_likes !== undefined && profile.total_likes !== null) {
                      metrics.push({ label: 'Total Likes', value: profile.total_likes, icon: Heart });
                    }
                    
                    // Core Performance metrics
                    if (profile.core_performance) {
                      const cp = profile.core_performance;
                      if (cp.reach) metrics.push({ label: 'Reach', value: cp.reach });
                      if (cp.total_videos) metrics.push({ label: 'Total Videos', value: cp.total_videos });
                      if (cp.impressions) metrics.push({ label: 'Impressions', value: cp.impressions });
                      if (cp.starts) metrics.push({ label: 'Starts', value: cp.starts });
                      if (cp.streams) metrics.push({ label: 'Streams', value: cp.streams });
                      if (cp.listeners) metrics.push({ label: 'Listeners', value: cp.listeners });
                      if (cp.ctr) metrics.push({ label: 'CTR', value: cp.ctr });
                    }
                    
                    // Engagement & Retention metrics
                    if (profile.engagement_retention) {
                      const er = profile.engagement_retention;
                      if (er.watch_time) metrics.push({ label: 'Watch Time', value: er.watch_time });
                      if (er.avg_view_duration) metrics.push({ label: 'Avg Duration', value: er.avg_view_duration });
                      if (er.retention_rate) metrics.push({ label: 'Retention Rate', value: er.retention_rate });
                      if (er.engagement_count) metrics.push({ label: 'Engagement', value: er.engagement_count });
                      if (er.follower_growth) metrics.push({ label: 'Follower Growth', value: er.follower_growth });
                      if (er.watched_full_video) metrics.push({ label: 'Full Video Views', value: er.watched_full_video });
                      if (er.avg_consumption) metrics.push({ label: 'Avg Consumption', value: er.avg_consumption });
                      if (er.engaged_listeners) metrics.push({ label: 'Engaged Listeners', value: er.engaged_listeners });
                    }
                    
                    return (
                      <div key={index} className="p-4 border border-border rounded-lg bg-card/40">
                        <div className="flex items-start gap-4">
                          {profile.profile_photo ? (
                            <img
                              src={profile.profile_photo}
                              alt={profile.handle}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-2xl font-bold text-primary">
                                {profile.handle.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                              <h4 className="font-semibold">{profile.handle.startsWith('@') ? profile.handle : `@${profile.handle}`}</h4>
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded capitalize">
                                {profile.platform}
                              </span>
                            </div>
                            
                            {metrics.length > 0 && (
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {metrics.map((metric, idx) => (
                                  <div key={idx}>
                                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                                      {metric.icon && <metric.icon className="w-3 h-3" />}
                                      {metric.label}
                                    </div>
                                    <div className="text-sm font-semibold">
                                      {typeof metric.value === 'number'
                                        ? metric.value.toLocaleString()
                                        : metric.value}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Demographics */}
                            {profile.demographics && (
                              <div className="mt-4 pt-4 border-t border-border">
                                <div className="text-xs font-medium text-muted-foreground mb-2">Demographics</div>
                                <div className="space-y-1 text-xs">
                                  {profile.demographics.top_locations && (
                                    <div>
                                      <span className="text-muted-foreground">Top Locations:</span>{' '}
                                      <span className="font-medium">
                                        {typeof profile.demographics.top_locations === 'string'
                                          ? profile.demographics.top_locations
                                          : typeof profile.demographics.top_locations === 'object' && profile.demographics.top_locations !== null
                                          ? Object.entries(profile.demographics.top_locations)
                                              .map(([key, value]) => `${key}: ${value}`)
                                              .join(', ')
                                          : 'N/A'}
                                      </span>
                                    </div>
                                  )}
                                  {profile.demographics.gender_split && typeof profile.demographics.gender_split === 'object' && (
                                    <div>
                                      <span className="text-muted-foreground">Gender Split:</span>{' '}
                                      <span className="font-medium">
                                        {Object.entries(profile.demographics.gender_split)
                                          .filter(([_, value]) => value !== null && value !== undefined)
                                          .map(([key, value]) => `${key}: ${value}`)
                                          .join(', ') || 'N/A'}
                                      </span>
                                    </div>
                                  )}
                                  {profile.demographics.user_status && (
                                    <div>
                                      <span className="text-muted-foreground">User Status:</span>{' '}
                                      <span className="font-medium">{profile.demographics.user_status}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Traffic & Discovery */}
                            {profile.traffic_discovery && (
                              <div className="mt-4 pt-4 border-t border-border">
                                <div className="text-xs font-medium text-muted-foreground mb-2">Traffic & Discovery</div>
                                <div className="space-y-1 text-xs">
                                  {profile.traffic_discovery.traffic_sources && (
                                    <div>
                                      <span className="text-muted-foreground">Traffic Sources:</span>{' '}
                                      <span className="font-medium">{profile.traffic_discovery.traffic_sources}</span>
                                    </div>
                                  )}
                                  {profile.traffic_discovery.device_types && (
                                    <div>
                                      <span className="text-muted-foreground">Device Types:</span>{' '}
                                      <span className="font-medium">{profile.traffic_discovery.device_types}</span>
                                    </div>
                                  )}
                                  {profile.traffic_discovery.listening_apps && (
                                    <div>
                                      <span className="text-muted-foreground">Listening Apps:</span>{' '}
                                      <span className="font-medium">{profile.traffic_discovery.listening_apps}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}